import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Mui from "@mui/material";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { clearEditUserData } from "services/Redux/userManagament";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { useFetch } from "services/hooks/useFetch";
import sessionHandling from "services/utils/notificationUtils";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { adminUi } from "../adminListScreenUi";
import {
  designColors,
  designRadii,
  designType,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel as AdminFieldLabel,
  GlobalNoticeBanner as AdminNoticeBanner,
  globalTextFieldSx as adminTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";

const EDIT_USER_NOTICE =
  "Update this user's system login details, company assignment, and access level.";

const FieldLabel = ({ children, tooltip }) => (
  <AdminFieldLabel helperText={tooltip}>{children}</AdminFieldLabel>
);

const ScopeNotice = () => (
  <AdminNoticeBanner
    sx={{ mb: 3 }}
    icon={
      <Mui.Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: designRadii.pill,
          backgroundColor: designColors.tealDark,
        }}
      />
    }
  >
    {EDIT_USER_NOTICE}
  </AdminNoticeBanner>
);

export const EditUser = () => {
  const editUser = useSelector((state) => state.userReducer.editUserData);
  const dispatch = useDispatch();
  const history = useHistory();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { result: companyDetails } = useFetch(`${domain}get-active-company/`);
  const [loading, setLoading] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      user_type: editUser?.role || "User",
      name: editUser?.user_name || "",
      phoneno: editUser?.phonenumber || "",
      email: editUser?.email || "",
      company: editUser?.company_name || "",
      company_id: editUser?.company_id || null,
    },
  });

  useEffect(() => {
    if (!editUser) return;
    setValue("user_type", editUser.role || "User");
    setValue("name", editUser.user_name || "");
    setValue("phoneno", editUser.phonenumber || "");
    setValue("email", editUser.email || "");
    setValue("company", editUser.company_name || "");
    setValue("company_id", editUser.company_id || null);
  }, [editUser, setValue]);

  const handleBack = () => {
    dispatch(clearEditUserData());
    history.push("/admin/administration/user");
  };

  const handleKeyPress = (event) => {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  };

  const checkSpecialChar = (event) => {
    if (!/^[a-zA-Z0-9 ]*$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleCompanyName = (event, data) => {
    if (data) {
      setValue("company", data?.label);
      setValue("company_id", data?.id);
      return;
    }
    setValue("company", "");
    setValue("company_id", null);
  };

  const handleUserType = (event) => {
    setValue("user_type", event.target.value);
  };

  const onSubmit = async (data) => {
    setButtonLoader(true);
    try {
      const response = await fetch(`${domain}normal_user_registration/`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          user_id: editUser.id,
          phone_number: data.phoneno,
          user_name: data.name,
          email: data.email,
          role: data?.user_type,
          company_id: data.company_id,
        }),
      });
      const res = await response.json();
      if (response.ok) {
        reset();
        showToast(
          "success",
          data?.user_type === "User"
            ? "User updated successfully"
            : "Super admin updated successfully",
        );
        setLoading(false);
        dispatch(setGlobalSearchValue(""));
        dispatch(clearEditUserData());
        history.push("/admin/administration/user");
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        showToast(
          "error",
          res?.error ||
            res?.message ||
            "Couldn't save the user details. Please try again.",
        );
      }
    } catch (error) {
      console.error("[EditUser] operation failed:", error);
      showToast("error", "Couldn't save the user details. Please try again.");
    } finally {
      setButtonLoader(false);
    }
  };

  if (!editUser) {
    return (
      <Mui.Box sx={{ padding: 4 }}>
        <Mui.Typography variant="h6" sx={{ color: designColors.slate800 }}>
          No user selected for editing.
        </Mui.Typography>
      </Mui.Box>
    );
  }

  const textFieldSx = adminTextFieldSx;

  const nameField = (
    <Mui.Box>
      <FieldLabel tooltip="Enter the full name of the user. This will be used to identify the individual in the system.">
        User Name
      </FieldLabel>
      <Mui.TextField
        {...register("name", {
          required: "Name is required",
          maxLength: {
            value: 30,
            message: "Maximum length is 30 characters",
          },
          minLength: {
            value: 3,
            message: "Minimum length is 3 characters",
          },
          validate: {
            noDigitsOrSpecial: (fieldValue) => {
              if (!/[A-Za-z]/.test(fieldValue)) {
                return "Name must contain at least one alphabet character.";
              }
              if (/^\d+$/.test(fieldValue)) {
                return "Name should not contain only digits.";
              }
              if (!/^[A-Za-z0-9\s&-]+$/.test(fieldValue)) {
                return "Name should only contain alphabets, spaces, -,numbers and & symbol.";
              }
              return true;
            },
            emailAvailable: async (fieldValue) => {
              if (
                fieldValue !== editUser?.user_name &&
                fieldValue !== ""
              ) {
                const res = await customGetAPI(
                  `${domain}check_username/?username=${fieldValue}`,
                );
                if (res?.ok) {
                  return res?.responseDetails?.exists
                    ? "Name Already Exists"
                    : true;
                }
              }
              return true;
            },
          },
        })}
        size="small"
        fullWidth
        error={Boolean(errors.name)}
        helperText={errors.name ? errors.name.message : null}
        placeholder="Enter the name"
        onKeyPress={checkSpecialChar}
        onBlur={() => trigger("name")}
        sx={textFieldSx}
      />
    </Mui.Box>
  );

  const phoneField = (
    <Mui.Box>
      <FieldLabel tooltip="Input the user's active mobile number. This may be used for notifications, login authentication, or user verification.">
        Phone Number
      </FieldLabel>
      <Mui.TextField
        {...register("phoneno", {
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Minimum length is 10 digits",
          },
          maxLength: {
            value: 15,
            message: "Maximum length is 15 digits",
          },
          validate: {
            employeePhonenumberAvailable: async (fieldValue) => {
              if (
                fieldValue !== editUser?.phonenumber &&
                fieldValue !== ""
              ) {
                const response = await fetch(
                  `${domain}unique/phone-number/?phone_no=${fieldValue}`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `token ${token}`,
                    },
                  },
                );
                const resp = await response.json();
                if (resp.exists) {
                  return "Phone number already exists";
                }
              }
              return true;
            },
          },
        })}
        size="small"
        fullWidth
        onKeyPress={handleKeyPress}
        placeholder="Enter the phone number"
        error={Boolean(errors.phoneno)}
        helperText={errors.phoneno ? errors.phoneno.message : null}
        sx={textFieldSx}
      />
    </Mui.Box>
  );

  const companyField = (
    <Mui.Box>
      <FieldLabel tooltip="Select the company the user is associated with from the dropdown list. This links the user to relevant departments, permissions, and data access.">
        Company Name
      </FieldLabel>
      <CustomAutoComplete
        dataList={companyDetails.map((obj) => ({
          ...obj,
          label: obj.companyname,
        }))}
        selectedValue={watch("company")}
        rules={{
          ...register("company", {
            required: "Company name is required",
          }),
        }}
        updateDataFunction={handleCompanyName}
        placeHolder="Select the company name"
        errorMessage={errors?.company?.message ? errors?.company?.message : ""}
        textBoxError={Boolean(errors.company)}
      />
    </Mui.Box>
  );

  const emailField = (
    <Mui.Box>
      <FieldLabel tooltip="Provide a valid email address for the user. The email is essential for communication, system alerts, and possibly login credentials.">
        Email
      </FieldLabel>
      <Mui.TextField
        {...register("email", {
          required: false,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid e-mail address",
          },
          validate: {
            emailAvailable: async (fieldValue) => {
              if (fieldValue !== editUser?.email && fieldValue !== "") {
                const res = await customGetAPI(
                  `${domain}email_verfication/?email=${fieldValue}`,
                );
                if (res?.ok) {
                  return res?.responseDetails?.exists
                    ? "Email Already Exists"
                    : true;
                }
              }
              return true;
            },
          },
        })}
        error={Boolean(errors.email)}
        helperText={errors.email ? errors.email.message : null}
        placeholder="Enter the email"
        size="small"
        fullWidth
        sx={textFieldSx}
      />
    </Mui.Box>
  );

  const userTypeField = (
    <Mui.Box>
      <FieldLabel tooltip="Choose whether this login should have standard user access or Super Admin access.">
        User Type
      </FieldLabel>
      <Mui.RadioGroup
        row
        onChange={handleUserType}
        aria-labelledby="edit-user-type-label"
        value={watch("user_type")}
        name="radio-buttons-group"
        sx={{
          gap: 2,
          "& .MuiFormControlLabel-label": {
            fontSize: 14,
            color: designColors.slate800,
          },
        }}
      >
        <Mui.FormControlLabel
          label="User"
          value="User"
          control={<Mui.Radio />}
        />
        <Mui.FormControlLabel
          label="Super Admin"
          value="Super Admin"
          control={<Mui.Radio />}
        />
      </Mui.RadioGroup>
    </Mui.Box>
  );

  return (
    <React.Fragment>
      <Mui.Box sx={{ ...adminUi.page, px: 2, pt: 2, pb: 4 }}>
        <Mui.Box
          sx={{
            maxWidth: 960,
            mx: "auto",
          }}
        >
          <Mui.Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <BackArrow actionFunction={handleBack} />
            <Mui.Typography sx={designType.pageTitle}>Edit User</Mui.Typography>
          </Mui.Stack>
          <Mui.Typography sx={{ ...designType.pageSubtitle, mb: 2.5 }}>
            Update this user's login and access details.
          </Mui.Typography>

          {loading ? (
            <Mui.Box>
              <Mui.Typography sx={adminUi.panelMutedText}>Loading...</Mui.Typography>
            </Mui.Box>
          ) : (
            <Mui.Box
              sx={{
                ...adminUi.sectionCard,
                p: { xs: 2, sm: 3 },
              }}
            >
              <ScopeNotice />

              <form onSubmit={handleSubmit(onSubmit)}>
                <Mui.Grid container spacing={3}>
                  <Mui.Grid item xs={12}>
                    {nameField}
                  </Mui.Grid>
                  <Mui.Grid item xs={12}>
                    {phoneField}
                  </Mui.Grid>
                  <Mui.Grid item xs={12} sm={6}>
                    {companyField}
                  </Mui.Grid>
                  <Mui.Grid item xs={12} sm={6}>
                    {emailField}
                  </Mui.Grid>
                  <Mui.Grid item xs={12}>
                    {userTypeField}
                  </Mui.Grid>
                </Mui.Grid>

                <Mui.Stack
                  direction="row"
                  spacing={1.5}
                  justifyContent="flex-end"
                  sx={{
                    ...adminUi.stickyFooterBar,
                    mt: 3,
                    px: 0,
                    py: 1.5,
                  }}
                >
                  <SecondaryButton onClick={handleBack}>Cancel</SecondaryButton>
                  <PrimaryButton
                    type="submit"
                    loading={buttonLoader}
                    disabled={buttonLoader}
                  >
                    Update
                  </PrimaryButton>
                </Mui.Stack>
              </form>
            </Mui.Box>
          )}
        </Mui.Box>
      </Mui.Box>
    </React.Fragment>
  );
};
